import { ChevronRight } from 'lucide-react';
import { type ComponentType, type ReactNode, Suspense, useState } from 'react';
import type { Task } from '../api/types';
import { cn } from '../lib/cn';
import { MODULES, type TaskAlert, type TaskCardTab, taskExt, useModuleAccess } from './modules';
import { useSession } from './session';

/**
 * Місця для розширень модулів у задачі. Модуль описує їх у `task` свого ModuleDefinition,
 * оболонка лише показує: у картці — розділи, у рядках і на картках — значки, у списку — колонки.
 * Для працівників без доступу до модуля й без даних модуля в задачі нічого не показується.
 */
// Перелік обчислюється під час виклику: модулі можуть імпортувати компоненти задач, а ті — цей файл.
const withTask = () => MODULES.filter((m) => m.task);
const withShell = () => MODULES.filter((m) => m.shell?.Topbar);

function useExtAccess() {
  const { can } = useSession();
  return useModuleAccess(can);
}

const tabOf = (m: (typeof MODULES)[number]): TaskCardTab => m.task?.panel?.tab ?? 'work';

/** Модулі, чий розділ показується в цій задачі цьому працівникові. */
function usePanelModules(task: Task) {
  const access = useExtAccess();
  return withTask().filter((m) => m.task?.panel && (taskExt(task, m.key) !== undefined || access.get(m.key)));
}

/** Які вкладки картки, крім «Роботи», мають розділи модулів у цій задачі. */
export function useTaskExtTabs(task: Task): Record<Exclude<TaskCardTab, 'work'>, boolean> {
  const panels = usePanelModules(task);
  return { management: panels.some((m) => tabOf(m) === 'management'), finance: panels.some((m) => tabOf(m) === 'finance') };
}

/**
 * Розділи модулів у картці задачі для однієї вкладки. `flat` — без згортання: уміст розділу одразу під назвою
 * (вкладка «Управління» цілком належить одному розділу, тож ще одна рамка зі стрілкою була б зайвою).
 * `only`: «filled» — розділи, у яких для цієї задачі вже є дані модуля (чек-лист етапу) — вони стоять біля опису;
 * «empty» — порожні й другорядні (ШІ-помічник): згорнуті, наприкінці картки.
 */
export function TaskExtPanels({ task, tab = 'work', flat = false, only }: { task: Task; tab?: TaskCardTab; flat?: boolean; only?: 'filled' | 'empty' }) {
  const all = usePanelModules(task).filter((m) => tabOf(m) === tab);
  const filled = (m: (typeof all)[number]) => taskExt(task, m.key) !== undefined && m.task!.panel!.defaultOpen !== false;
  const panels = only ? all.filter((m) => filled(m) === (only === 'filled')) : all;
  if (!panels.length) return null;
  return (
    <>
      {panels.map((m) => {
        const panel = m.task!.panel!;
        const Component = panel.Component;
        const body = (
          <Suspense fallback={null}>
            <Component task={task} ext={taskExt(task, m.key)} />
          </Suspense>
        );
        if (flat && panels.length === 1) return <div key={m.key}>{body}</div>;
        return (
          <ExtPanel key={m.key} title={panel.title} icon={panel.icon} defaultOpen={flat || only === 'filled' || (only === undefined && panel.defaultOpen !== false)}>
            {body}
          </ExtPanel>
        );
      })}
    </>
  );
}

function ExtPanel({ title, icon: Icon, children, defaultOpen = true }: { title: string; icon?: ComponentType<{ className?: string }>; children: ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section aria-label={title} className="rounded-md border border-line">
      <h3>
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className="flex w-full items-center gap-2 px-3 py-2 text-left text-meta font-semibold text-fg-2 hover:bg-surface-2"
        >
          <ChevronRight className={cn('ers-turn size-4 text-fg-3', open && 'rotate-90')} aria-hidden />
          {Icon && <Icon className="size-4 text-fg-3" />}
          {title}
        </button>
      </h3>
      {open && <div className="border-t border-line px-3 py-3">{children}</div>}
    </section>
  );
}

/** Значки модулів у рядку переліку чи на картці дошки (лише коли в задачі є дані модуля). */
export function TaskExtBadges({ task, variant }: { task: Task; variant: 'row' | 'card' }) {
  const items = withTask().filter((m) => m.task?.Badges && taskExt(task, m.key) !== undefined);
  if (!items.length) return null;
  return (
    <>
      {items.map((m) => {
        const Badges = m.task!.Badges!;
        return <Badges key={m.key} task={task} ext={taskExt(task, m.key)} variant={variant} />;
      })}
    </>
  );
}

/** Попередження модулів про задачу (лише коли в задачі є дані модуля) — для ієрархії на картці дошки. */
export function taskExtAlerts(task: Task): TaskAlert[] {
  return withTask().flatMap((m) => {
    const ext = taskExt(task, m.key);
    return m.task?.alerts && ext !== undefined ? m.task.alerts({ task, ext }) : [];
  });
}

/** Додаткові колонки подання «Список» від модулів, доступних користувачу. */
export function useExtColumns() {
  const access = useExtAccess();
  return withTask().filter((m) => access.get(m.key)).flatMap((m) => (m.task?.columns ?? []).map((c) => ({ ...c, moduleKey: m.key })));
}

export type ExtColumn = ReturnType<typeof useExtColumns>[number];

export function TaskExtCells({ task, columns }: { task: Task; columns: ExtColumn[] }) {
  if (!columns.length) return null;
  return (
    <>
      {columns.map((c) => (
        <span key={`${c.moduleKey}-${c.key}`} className="relative z-[1] hidden shrink-0 truncate text-meta text-fg-2 lg:block" style={{ width: c.width }}>
          <c.Cell task={task} ext={taskExt(task, c.moduleKey)} />
        </span>
      ))}
    </>
  );
}

/** Кнопки модулів у шапці картки задачі (напр. таймер). */
export function TaskExtToolbar({ task }: { task: Task }) {
  const access = useExtAccess();
  const items = withTask().filter((m) => m.task?.Toolbar && access.get(m.key));
  if (!items.length) return null;
  return (
    <>
      {items.map((m) => {
        const C = m.task!.Toolbar!;
        return (
          <Suspense key={m.key} fallback={null}>
            <C task={task} ext={taskExt(task, m.key)} />
          </Suspense>
        );
      })}
    </>
  );
}

/** Рядки модулів у сітці реквізитів задачі. */
export function TaskExtFieldRows({ task }: { task: Task }) {
  const access = useExtAccess();
  const items = withTask().filter((m) => m.task?.Fields && access.get(m.key));
  if (!items.length) return null;
  return (
    <>
      {items.map((m) => {
        const C = m.task!.Fields!;
        return (
          <Suspense key={m.key} fallback={null}>
            <C task={task} ext={taskExt(task, m.key)} />
          </Suspense>
        );
      })}
    </>
  );
}

/** Елементи модулів у верхній панелі оболонки. */
export function ShellTopbarSlots() {
  const access = useExtAccess();
  const items = withShell().filter((m) => access.get(m.key));
  if (!items.length) return null;
  return (
    <>
      {items.map((m) => {
        const C = m.shell!.Topbar!;
        return (
          <Suspense key={m.key} fallback={null}>
            <C />
          </Suspense>
        );
      })}
    </>
  );
}
